/**
 * Fixed Assets & Depreciation tab — new in v2.
 *
 * The template's two roll-forwards, Intangible and Tangible, and a note for
 * each class declared beside them - IAS 16 paragraph 73 asks for the
 * reconciliation by class, and a filing that discloses buildings, plant and
 * vehicles separately should not read as one number. Every note has the same
 * shape:
 *   Cost:   b/fwd + additions - disposals = c/fwd
 *   Charge: b/fwd + charge    - disposals = c/fwd
 *   NBV = cost c/fwd - charge c/fwd
 *
 * b/fwd is COMPUTED, not editable: only the first period has an opening
 * balance of its own, and every later period is the previous c/fwd. Making the
 * row editable would invite entering a figure that the next recalculation
 * overwrites.
 *
 * Disposals are entered NEGATIVE, matching the template.
 *
 * This replaces v1's single 'fixed asset additions' field.
 */

import { useCallback } from 'react';
import { ComputedDataRow } from '../table/DataRow';
import { V2EditableRow } from './V2EditableRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type {
  AssetClassComputed,
  AssetClassInputs,
} from '../../../types/financial-model-v2';
import { V2Sheet } from './V2Table';

interface AssetBlockProps {
  title: string;
  chargeLabel: string;
  inputs: AssetClassInputs;
  computed: AssetClassComputed;
  path: string;
  onChange: (path: string) => (period: string, value: number) => void;
}

function AssetBlock({
  title,
  chargeLabel,
  inputs,
  computed,
  path,
  onChange,
}: AssetBlockProps) {
  return (
    <>
      <SectionHeader label={`${title} — Cost`} />
      <ComputedDataRow label="Cost b/fwd" data={computed.costBfwd} />
      <V2EditableRow
        label="Additions"
        data={inputs.additions}
        onCellChange={onChange(`${path}.additions`)}
      />
      <V2EditableRow
        label="Disposals (negative)"
        data={inputs.disposalsCost}
        onCellChange={onChange(`${path}.disposalsCost`)}
      />
      <SubtotalRow label="Cost c/fwd" data={computed.costCfwd} />

      <SectionHeader label={`${title} — ${chargeLabel}`} />
      <ComputedDataRow label={`${chargeLabel} b/fwd`} data={computed.chargeBfwd} />
      {/* Shows the charge the engine used: a blank forecast year gets the
          straight-line default from Settings, and typing a value overrides it. */}
      <V2EditableRow
        label={`${chargeLabel} charge (blank forecast = straight-line default)`}
        data={computed.charge}
        onCellChange={onChange(`${path}.charge`)}
      />
      <V2EditableRow
        label="Disposals (negative)"
        data={inputs.disposalsCharge}
        onCellChange={onChange(`${path}.disposalsCharge`)}
      />
      <SubtotalRow label={`${chargeLabel} c/fwd`} data={computed.chargeCfwd} />

      <GrandTotalRow label={`${title} NBV`} data={computed.nbv} />
    </>
  );
}

export function FixedAssetsView() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );

  if (!model) return null;
  const { inputs, computed } = model;

  return (
    <V2Sheet>
      <AssetBlock
        title="Intangible Assets"
        chargeLabel="Amortisation"
        inputs={inputs.fixedAssets.intangible}
        computed={computed.fixedAssets.intangible}
        path="fixedAssets.intangible"
        onChange={handleChange}
      />
      <AssetBlock
        title="Tangible Assets"
        chargeLabel="Depreciation"
        inputs={inputs.fixedAssets.tangible}
        computed={computed.fixedAssets.tangible}
        path="fixedAssets.tangible"
        onChange={handleChange}
      />

      {/* A note per declared class. Only the classes that hold something
          are computed, so the tab shows two notes for a model built from the
          template and one more for every class a filing disclosed. */}
      {Object.entries(computed.fixedAssets.classes ?? {}).map(([key, cls]) => {
        const own = inputs.fixedAssets.classes?.[key];
        if (!own) return null;
        return (
          <AssetBlock
            key={key}
            title={cls.caption || key}
            chargeLabel={cls.intangible ? 'Amortisation' : 'Depreciation'}
            inputs={own}
            computed={cls}
            path={`fixedAssets.classes.${key}`}
            onChange={handleChange}
          />
        );
      })}

      <SectionHeader label="Totals" />
      <ComputedDataRow label="Gross Fixed Assets" data={computed.fixedAssets.grossFixedAssets} />
      <ComputedDataRow
        label="Accumulated Depreciation"
        data={computed.fixedAssets.accumulatedDepreciation}
      />
      <GrandTotalRow label="Net Fixed Assets" data={computed.fixedAssets.netFixedAssets} />
    </V2Sheet>
  );
}
